import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useMemo } from "react";
import ColorService from "../../../services/ColorService";

const TAG_KEY = "common";

/** Resolve count for a canonical state key from API or loosely keyed map */
function countForState(raw, state) {
    if (!raw || typeof raw !== "object") return 0;
    if (raw[state] != null && typeof raw[state] !== "object") {
        return Number(raw[state]) || 0;
    }
    const upper = state.toUpperCase();
    const hit = Object.entries(raw).find(([k]) => String(k).toUpperCase().replace(/\s+/g, "_") === upper);
    return hit ? Number(hit[1]) || 0 : 0;
}

/**
 * @param {Object} [props.tagCountsByApi] — from GET .../tag-counts-by-state; when set (object), used as primary source
 * @param {boolean} props.apiLoading
 * @param {boolean} props.apiFailed — request failed (fallback to group-by-state countByTag if any)
 */
export default function CommonTagByStateStatsPanel({
    stateKeys,
    groupByStateStats,
    tagCountsByApi,
    apiLoading = false,
    apiFailed = false,
    tagLabel = "common"
}) {
    const { counts, source, hasFallbackTagBucket } = useMemo(() => {
        if (tagCountsByApi != null && typeof tagCountsByApi === "object") {
            const out = {};
            (stateKeys || []).forEach((state) => {
                out[state] = countForState(tagCountsByApi, state);
            });
            return { counts: out, source: "api", hasFallbackTagBucket: false };
        }
        const out = {};
        let anyBucket = false;
        (stateKeys || []).forEach((state) => {
            const raw = groupByStateStats?.[state]?.countByTag;
            if (raw && typeof raw === "object" && Object.keys(raw).length > 0) {
                anyBucket = true;
            }
            const entry =
                raw && typeof raw === "object"
                    ? Object.entries(raw).find(([k]) => String(k).toLowerCase() === TAG_KEY)
                    : null;
            out[state] = entry ? Number(entry[1]) || 0 : 0;
        });
        return { counts: out, source: "groupByState", hasFallbackTagBucket: anyBucket };
    }, [groupByStateStats, stateKeys, tagCountsByApi]);

    const total = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);
    const showNoDataHint =
        !apiLoading && !apiFailed && source === "groupByState" && !hasFallbackTagBucket;
    const showApiError = !apiLoading && apiFailed && source === "groupByState" && !hasFallbackTagBucket;

    if (apiLoading && tagCountsByApi == null) {
        return (
            <Box sx={{ flex: 1, width: "100%", py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Crossword templates tagged <strong>{tagLabel}</strong>, by state (loading tag-counts-by-state).
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                        Loading tag counts…
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ flex: 1, width: "100%" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Crossword templates tagged <strong>{tagLabel}</strong>, by state
                {source === "api" && " (from tag-counts-by-state)"}.
                {source === "groupByState" && hasFallbackTagBucket && " (from group-by-state countByTag)."}
            </Typography>
            {showApiError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Could not load <code>/v1/stats/cross/tag-counts-by-state</code>. Counts below are from group-by-state
                    only if <code>countByTag</code> is present; otherwise they are zero.
                </Alert>
            )}
            {!apiLoading && apiFailed && hasFallbackTagBucket && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Tag-counts API failed; showing <code>countByTag</code> from group-by-state instead.
                </Alert>
            )}
            {showNoDataHint && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" component="span">
                        No <code>countByTag</code> on group-by-state and tag-counts API was not used. Ensure{" "}
                        <code>GET /v1/stats/cross/tag-counts-by-state?tag=common&amp;puzzleType=CROSSWORD&amp;language=…</code>{" "}
                        is available, or add <code>countByTag</code> per state on group-by-state.
                    </Typography>
                </Alert>
            )}
            <Grid container spacing={2}>
                {(stateKeys || []).map((state) => {
                    const n = counts[state] ?? 0;
                    const stateColor = ColorService.getStateColor?.(state.toLowerCase()) || "#666";
                    const label = state.replace(/_/g, " ");
                    return (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={state}>
                            <Card variant="outlined" sx={{ borderLeft: "4px solid", borderLeftColor: stateColor, height: "100%" }}>
                                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, textTransform: "capitalize" }}>
                                        {label}
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            color: n === 0 ? "error.main" : "text.primary"
                                        }}
                                    >
                                        {n}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {tagLabel} tag
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
            {!apiLoading && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                    Total across listed states: <strong>{total}</strong>
                </Typography>
            )}
        </Box>
    );
}
